export interface DashletRenderEntry {
  element: HTMLElement;
  settled: boolean;
}

const entries = new Map<string, DashletRenderEntry>();

export const getDashletRender = (
  dashletId: string
): DashletRenderEntry | undefined => entries.get(dashletId);

export const removeDashletRender = (
  dashletId: string,
  element: HTMLElement
) => {
  if (entries.get(dashletId)?.element === element) entries.delete(dashletId);
};

export const afterPaint = (callback: () => void): (() => void) => {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    callback();
  };
  let frame = 0;
  let task: ReturnType<typeof setTimeout> | undefined;
  const fallback = setTimeout(run, 250);
  frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(() => {
      task = setTimeout(run, 0);
    });
  });
  return () => {
    done = true;
    clearTimeout(fallback);
    clearTimeout(task);
    cancelAnimationFrame(frame);
  };
};

export interface DashletRenderTracker {
  contentCommitted: (element: HTMLElement | null) => void;
  pendingChanged: (pending: boolean) => void;
  dispose: () => void;
}

export const createDashletRenderTracker = (
  dashletId: string
): DashletRenderTracker => {
  let element: HTMLElement | null = null;
  let pending = false;
  let sawPending = false;
  let checked = false;
  let cancelCheck: (() => void) | null = null;

  const publish = (settled: boolean) => {
    if (!element) return;
    entries.set(dashletId, { element, settled });
  };

  return {
    contentCommitted: (nextElement) => {
      cancelCheck?.();
      if (element && element !== nextElement) {
        removeDashletRender(dashletId, element);
      }
      element = nextElement;
      sawPending = false;
      checked = false;
      publish(false);
      cancelCheck = afterPaint(() => {
        cancelCheck = null;
        checked = true;
        if (!pending) publish(true);
      });
    },
    pendingChanged: (nextPending) => {
      pending = nextPending;
      if (nextPending) {
        sawPending = true;
        publish(false);
      } else if (sawPending || checked) {
        publish(true);
      }
    },
    dispose: () => {
      cancelCheck?.();
      cancelCheck = null;
      if (element) removeDashletRender(dashletId, element);
      element = null;
    },
  };
};
